import { c as _c } from "react/compiler-runtime";
import * as React from "react";
import { iconMap } from "./icon-map";
import type { IconBaseProps } from "./icon-base";
import type { IconName } from "./types";

/**
 * Icon Component
 *
 * Design System: V&A Waterfront Foundation Design System
 * Figma: https://www.figma.com/design/CV2ePEn9S0SSZ7T3AAPqF4/V-A-Waterfront---Website---Foundation-Design-System?node-id=9835-35340
 *
 * @example
 * // Basic usage
 * <Icon name="search" />
 *
 * // With size variant
 * <Icon name="close" size="sm" />
 *
 * // Filled variant
 * <Icon name="check" filled />
 *
 * // Custom className
 * <Icon name="menu" className="text-icon-primary" />
 *
 * // Non-decorative (for accessibility)
 * <Icon name="warning" decorative={false} aria-label="Warning" />
 */

export interface IconProps extends Omit<IconBaseProps, "children"> {
  /**
   * The name of the icon to render
   */
  name: IconName;
  /**
   * If true, renders the filled variant of the icon
   * @default false
   */
  filled?: boolean;
}
const Icon = React.forwardRef<SVGSVGElement, IconProps>((t0, ref) => {
  const $ = _c(9);
  let name;
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      name,
      filled: t1,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = name;
    $[2] = props;
    $[3] = t1;
  } else {
    name = $[1];
    props = $[2];
    t1 = $[3];
  }
  const filled = t1 === undefined ? false : t1;
  const IconComponent = iconMap[name];
  if (!IconComponent) {
    return null;
  }
  const t2 = filled ? "filled" : "outlined";
  let t3;
  if ($[4] !== IconComponent || $[5] !== props || $[6] !== ref || $[7] !== t2) {
    t3 = <IconComponent ref={ref} variant={t2} {...props} />;
    $[4] = IconComponent;
    $[5] = props;
    $[6] = ref;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3;
});
Icon.displayName = "Icon";
export { Icon };